import ArrowCircleDownOutlinedIcon from "@mui/icons-material/ArrowCircleDownOutlined";
import ArrowCircleRightOutlinedIcon from "@mui/icons-material/ArrowCircleRightOutlined";
import ArrowCircleUpOutlinedIcon from "@mui/icons-material/ArrowCircleUpOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import XIcon from "@mui/icons-material/X";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Grid,
  Stack,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import Axios from "axios";
import React, { Suspense, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import ArticleItem from "../../components/article-item/article-item";
import Layout from "../../components/layout/layout";
import ArticlesGrid from "../../components/content-grids/article-grid";
import XPostsGrid from "../../components/content-grids/xposts-grid";
import FacebookPostsGrid from "../../components/content-grids/facebook-grid";
import InstagramPostsGrid from "../../components/content-grids/instagram-grid";
import { envEndPoints } from "../../config";
import "./client-item.css";
import EditClient from "./edit-client";

const ClientItem = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [client, setClient] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [socialHandles, setSocialHandles] = useState({ twitter: [], facebook: [], instagram: [] });
  const [isClientDrawerOpen, setIsClientDrawerOpen] = useState(false);
  const [isArticleDrawerOpen, setIsArticleDrawerOpen] = useState(false);
  const [isReportDrawerOpen, setIsReportDrawerOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [generatedFilePath, setGeneratedFilePath] = useState(null);
  const [confirmationInput, setConfirmationInput] = useState("");
  const [formData, setFormData] = useState({
    url: "",
    client_id: id,
  });
  const [reportData, setReportData] = useState({
    start_date: "",
    end_date: "",
  });
  const [reportFormErrors, setReportFormErrors] = useState({
    startDate: false,
    endDate: false,
  });
  const [selectedReportType, setSelectedReportType] = useState("content"); // default report type
  const [clientUpdateSuccessOpen, setClientUpdateSuccessOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [contentView, setContentView] = useState("media"); // "media" | "xposts" | "facebook" | "instagram"

  const typeOrder = [
    "Company",
    "Regions",
    "Products",
    "Spokespersons",
    "Competitors",
    "Industries",
    "Exclusions",
  ];

  const sortTypesByCustomOrder = (types) => {
    return types.sort((a, b) => {
      return typeOrder.indexOf(a) - typeOrder.indexOf(b);
    });
  };

  const fetchHandles = async () => {
    const authHeaders = { headers: getAuthHeader() };
    const safeGet = (url) => Axios.get(url, authHeaders).catch(() => ({ data: [] }));
    const [xRes, fbRes, igRes] = await Promise.all([
      safeGet(`${envEndPoints().baseUrl}/client-xhandles/?client_id=${id}`),
      safeGet(`${envEndPoints().baseUrl}/client-facebook-handles/?client_id=${id}`),
      safeGet(`${envEndPoints().baseUrl}/client-instagram-handles/?client_id=${id}`),
    ]);
    setSocialHandles({
      twitter: xRes.data[0]?.handles?.map(h => h.handle) || [],
      facebook: fbRes.data[0]?.handles?.map(h => h.handle) || [],
      instagram: igRes.data[0]?.handles?.map(h => h.handle) || [],
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientResponse = await Axios.get(
          `${envEndPoints().baseUrl}/clients/?client_id=${id}`
        );
        const keywordsResponse = await Axios.get(
          `${envEndPoints().baseUrl}/client-keywords/?client_id=${id}`
        );
        setClient(clientResponse.data[0]);
        setKeywords(keywordsResponse.data[0]?.keywords || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
    fetchHandles();
  }, [id]);

  const refetchClientData = async () => {
    try {
      const clientResponse = await Axios.get(
        `${envEndPoints().baseUrl}/clients/?client_id=${id}`
      );
      const keywordsResponse = await Axios.get(
        `${envEndPoints().baseUrl}/client-keywords/?client_id=${id}`
      );
      setClient(clientResponse.data[0]);
      setKeywords(keywordsResponse.data[0]?.keywords || []);
      await fetchHandles();
    } catch (error) {
      console.error("Error refetching data:", error);
    }
  };

  const handleEditSaveSuccess = async () => {
    await refetchClientData();
    setClientUpdateSuccessOpen(true);
  };

  const keywordsByType = keywords.reduce((acc, keyword) => {
    if (keyword && keyword.type) {
      if (acc[keyword.type]) {
        acc[keyword.type].push(keyword.name);
      } else {
        acc[keyword.type] = [keyword.name];
      }
    }
    return acc;
  }, {});

  const totalCount = keywords.length;

  const handleDrawerClose = (drawerType) => {
    if (drawerType === "client") {
      setIsClientDrawerOpen(false);
    } else if (drawerType === "article") {
      setIsArticleDrawerOpen(false);
      setRefreshKey(k => k + 1);
    } else if (drawerType === "report") {
      setIsReportDrawerOpen(false);
    }
  };

  const handleEditClick = () => {
    setIsArticleDrawerOpen(false);
    setIsClientDrawerOpen(true);
  };

  const handleReportClick = () => {
    setIsReportDrawerOpen(true);
  };

  const handleDeleteDialogToggle = () => {
    setIsDeleteDialogOpen(!isDeleteDialogOpen);
  };

  const handleAddDialogToggle = () => {
    setIsAddDialogOpen(!isAddDialogOpen);
  };

  const handleUploadDialogToggle = (clientId) => {
    setFormData({ file: null, client_id: clientId });
    setIsUploadDialogOpen(!isUploadDialogOpen);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    const file = formData.file;
    if (!file) return;

    const fileType = file.name.split(".").pop().toLowerCase();
    let endpoint = "";

    if (fileType === "csv") {
      endpoint = `${envEndPoints().baseUrl}/bulk-upload/`;
    } else if (fileType === "eml" || fileType === "msg") {
      endpoint = `${envEndPoints().baseUrl}/upload-emails/`;
    } else {
      alert("Unsupported file type. Please upload a .eml, .msg, or .csv file.");
      return;
    }

    const uploadData = new FormData();
    uploadData.append("file", file);
    uploadData.append("client_id", formData.client_id);

    try {
      setIsLoading(true);
      await Axios.post(endpoint, uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Upload successful!");
      setFormData({ file: null });
      handleUploadDialogToggle();
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Upload failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleReportFormChange = (event) => {
    setReportData({
      ...reportData,
      [event.target.name]: event.target.value,
    });
    setReportFormErrors({ ...reportFormErrors, [event.target.name]: false });
  };

  const handleReportTypeChange = (type) => {
    setSelectedReportType(type);
  };

  const handleReportSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    // Define mappings for API endpoints and default filenames based on report type
    const apiEndpoints = {
      content: `${envEndPoints().baseUrl}/gen-keythemes-content/`,
      slides: `${envEndPoints().baseUrl}/report/generate`,
      excel: `${envEndPoints().baseUrl}/report/generate-excel/`,
    };

    const defaultFilenames = {
      content: "keythemes_content.docx",
      slides: "report.pptx",
      excel: "report.xlsx",
    };

    const apiEndpoint = apiEndpoints[selectedReportType];
    const defaultFilename = defaultFilenames[selectedReportType];

    if (!apiEndpoint || !defaultFilename) {
      alert("Invalid report type selected.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await Axios.post(
        apiEndpoint,
        {
          client_id: id,
          start_date: reportData.start_date,
          end_date: reportData.end_date,
        },
        {
          responseType: "blob", // Important: tells Axios to handle the response as a Blob
        }
      );

      // Create a Blob from the response data
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      // Create a URL for the Blob
      const url = window.URL.createObjectURL(blob);
      // Create a link element
      const link = document.createElement("a");
      link.href = url;

      // Extract filename from the response headers
      const contentDisposition = response.headers["content-disposition"];
      let filename = defaultFilename; // Default filename with correct extension
      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }
      link.setAttribute("download", filename);

      // Append link to the body
      document.body.appendChild(link);
      // Trigger the download
      link.click();
      // Clean up and remove the link
      window.URL.revokeObjectURL(url);
      link.remove();

      alert("Report generated successfully!");
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Error generating report.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    const response = await fetch(`${envEndPoints().baseUrl}/add-article/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });
    setIsLoading(false);
    const data = await response.json();
    console.log("Add article response:", data);
    if (response.status === 200) {
      alert(data.message || "Article added successfully.");
      setOpen(false);
      setFormData({ ...formData, url: "" });
    } else if (response.status === 409) {
      alert(data.message || "Article already exists in the database.");
      setOpen(false);
      setFormData({ ...formData, url: "" });
    } else {
      alert("Failed to add article");
    }
  };

  const handleDelete = async () => {
    const clientName = client?.name || "Client";
    try {
      const response = await Axios.post(
        `${envEndPoints().baseUrl}/clients/delete/`,
        {
          client_id: id,
        }
      );
      if (response.status === 200) {
        console.log("Delete successful");
        setIsDeleteDialogOpen(false);
        setConfirmationInput("");
        navigate("/clients", { state: { deletedClientName: clientName } });
      } else {
        console.error("Failed to delete client", response);
        alert("Failed to delete client.");
      }
    } catch (error) {
      console.error("Error deleting client:", error);
      alert("Error during deletion process.");
    }
  };

  const handleRowClick = (article) => {
    setSelectedArticle(article);
    setIsArticleDrawerOpen(true);
    setIsClientDrawerOpen(false);
  };

  const handleConfirmationInputChange = (event) => {
    setConfirmationInput(event.target.value);
  };

  const handleDeleteConfirm = (event) => {
    event.preventDefault();
    if (confirmationInput === "DELETE") {
      handleDelete();
    } else {
      alert("Please type 'DELETE' to confirm.");
    }
  };

  return (
    <Layout>
      <Snackbar
        open={clientUpdateSuccessOpen}
        autoHideDuration={4000}
        onClose={() => setClientUpdateSuccessOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setClientUpdateSuccessOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Client updated successfully.
        </Alert>
      </Snackbar>
      <Grid container spacing={3}>
        <Grid item md={3}>
          <Typography variant="h2">
            {client ? client.name : "Loading..."}
          </Typography>
        </Grid>

        <Grid item md={7}>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              Overall Sentiment
              <br />
              {client ? (
                <Chip
                  icon={
                    client.sentiment === "Positive" ? (
                      <ArrowCircleUpOutlinedIcon />
                    ) : client.sentiment === "Negative" ? (
                      <ArrowCircleDownOutlinedIcon />
                    ) : client.sentiment === "Neutral" ? (
                      <ArrowCircleRightOutlinedIcon />
                    ) : (
                      <HelpOutlineOutlinedIcon />
                    )
                  }
                  label={client.sentiment ? client.sentiment : "Unknown"}
                  className={`chip-${
                    client.sentiment
                      ? client.sentiment.toLowerCase()
                      : "unknown"
                  }-overall`}
                />
              ) : (
                <Chip label="Loading..." />
              )}
            </Grid>

            <Grid item xs={6} md={3}>
              Owners
              <br />
              <Stack direction="row" spacing={1} flexWrap={"wrap"}>
                {client ? (
                  client.owners.map((owner, index) => (
                    <Chip key={index} label={owner} className="chip-owners" />
                  ))
                ) : (
                  <Chip label="Loading..." />
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              Industries
              <br />
              <Stack direction="row" spacing={1} flexWrap={"wrap"}>
                {client ? (
                  client.industries.map((industry, index) => (
                    <Chip
                      key={index}
                      label={industry}
                      className="chip-clientIndustry"
                    />
                  ))
                ) : (
                  <Chip label="Loading..." />
                )}
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid item md={2}>
          <Grid container justifyContent="flex-end">
            <ButtonGroup
              variant="outlined"
              size="small"
              aria-label="Small button group"
            >
              <Button onClick={handleDeleteDialogToggle}>
                <DeleteSweepOutlinedIcon />
                &nbsp;
              </Button>
              <Button onClick={() => handleUploadDialogToggle(client.id)}>
                <UploadFileIcon />
                Upload
              </Button>
              <Button onClick={handleAddDialogToggle}>
                <NewspaperOutlinedIcon />
                &nbsp;Add
              </Button>
              {/* This is where the edit client button is*/}
              <Button onClick={handleEditClick}>
                <EditOutlinedIcon />
                &nbsp;Edit
              </Button>
              {/* This is where the edit client button is*/}
              <Button onClick={handleReportClick}>
                <NewspaperOutlinedIcon />
                &nbsp;Report
              </Button>
            </ButtonGroup>
          </Grid>
        </Grid>
        <Grid item md={12}>
          <Typography variant="body1">
            {client ? client.description : "Loading..."}
          </Typography>
        </Grid>

        {/* Toggle between Media Articles table and X posts table */}
        <Grid item xs={12} md={9} order={{ xs: 1, md: 2 }}>
          <ButtonGroup
            variant="outlined"
            size="small"
            sx={{
              mb: 1,
              "& .MuiButton-root": {
                borderColor: "#004c6d",
                color: "#004c6d",
              },
              "& .MuiButton-root:hover": {
                borderColor: "#003d57",
                backgroundColor: "rgba(0, 76, 109, 0.08)",
              },
              "& .MuiButton-contained": {
                backgroundColor: "#004c6d",
                color: "#fff",
                borderColor: "#004c6d",
              },
              "& .MuiButton-contained:hover": {
                backgroundColor: "#003d57",
                borderColor: "#003d57",
              },
            }}
            aria-label="Content view toggle"
          >
            {/* Check if contentView is equal to 'media', if so, update UI and set content view to "media" */}
            <Button
              variant={contentView === "media" ? "contained" : "outlined"}
              onClick={() => setContentView("media")}
            >
              <NewspaperOutlinedIcon sx={{ mr: 0.5 }} />
              Media Articles
            </Button>

            {/* Check if contentView is equal to 'xposts', if so, update UI and set content view to "xposts" */}
            <Button
              variant={contentView === "xposts" ? "contained" : "outlined"}
              onClick={() => setContentView("xposts")}
            >
              <XIcon sx={{ mr: 0.5 }} />
              X posts (Previously Tweets)
            </Button>
            {/* Check if contentView is equal to 'Facebook', if so, update UI and set content view to "Facebook" */}
            <Button
              variant={contentView === "facebook" ? "contained" : "outlined"}
              onClick={() => setContentView("facebook")}
            >
              <FacebookIcon sx={{ mr: 0.5 }} />
              Facebook Posts
            </Button>
            {/* Check if contentView is equal to 'Instagram', if so, update UI and set content view to "Instagram" */}
            <Button
              variant={contentView === "instagram" ? "contained" : "outlined"}
              onClick={() => setContentView("instagram")}
            >
              <InstagramIcon sx={{ mr: 0.5 }} />
              Instagram Posts
            </Button>
          </ButtonGroup>

          {/* End of toggling functionality */}

          
          {/* Render grid based on content view: media articles, X posts, or Facebook posts */}
          {contentView === "media" && (
            <ArticlesGrid
              clientId={id}
              onArticleSelect={handleRowClick}
              setSelectedArticle={setSelectedArticle}
              setIsArticleDrawerOpen={setIsArticleDrawerOpen}
              setIsClientDrawerOpen={setIsClientDrawerOpen}
              refreshKey={refreshKey}
            />
          )}
          {contentView === "xposts" && <XPostsGrid clientId={id} />}
          {contentView === "facebook" && <FacebookPostsGrid clientId={id} />}
          {contentView === "instagram" && <InstagramPostsGrid clientId={id} />}
        </Grid>

        <Grid item xs={12} md={3} order={{ xs: 2, md: 1 }}>
          {/* Where the original keywords card is rendered */}
          <Card>
            <CardContent>
              <Typography
                sx={{ fontSize: 14 }}
                color="text.secondary"
                gutterBottom
              >
                {client ? `${client.name} Keywords` : "Loading Keywords..."}
              </Typography>
              {client && (
                <>
                  <Typography variant="h2" sx={{ marginBottom: 2 }}>
                    {totalCount}
                  </Typography>
                  {sortTypesByCustomOrder(Object.keys(keywordsByType)).map(
                    (type, index, typesArray) => (
                      <React.Fragment key={type}>
                        <Stack spacing={1} sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ minWidth: 100 }}>
                            {type}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {keywordsByType[type].map(
                              (keyword, keywordIndex) => (
                                <Chip
                                  label={keyword}
                                  key={keywordIndex}
                                  className={`chip-${type.toLowerCase()}`}
                                />
                              )
                            )}
                          </Stack>
                        </Stack>
                        {index < typesArray.length - 1 && <hr />}
                      </React.Fragment>
                    )
                  )}
                </>
              )}
            </CardContent>
          </Card>
          {/* This is where the keywords card ends. */}
          {/* This is where the social media handles card starts. */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
                {client ? `${client.name} Social Media Handles` : "Loading Social Media Handles..."}
              </Typography>
              
              {client && (() => {
                const twitterHandles = socialHandles.twitter;
                const facebookHandles = socialHandles.facebook;
                const instagramHandles = socialHandles.instagram;
                const totalHandlesCount = twitterHandles.length + facebookHandles.length + instagramHandles.length;

                return (
                  <>
                    <Typography variant="h2" sx={{ marginBottom: 2 }}>
                      {totalHandlesCount}
                    </Typography>

                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ minWidth: 100 }}>
                        X (Previously Twitter)
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {twitterHandles.map((handle, index) => (
                          <Chip
                            key={index}
                            label={handle.startsWith("@") ? handle : `@${handle}`}
                            className="chip-company"
                          />
                        ))}
                      </Stack>
                    </Stack>
                    <hr />
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ minWidth: 100 }}>
                        Facebook
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {facebookHandles.map((handle, index) => (
                          <Chip
                            key={`fb-${index}`}
                            label={handle.startsWith("@") ? handle : `@${handle}`}
                            className="chip-facebook"
                          />
                        ))}
                      </Stack>
                    </Stack>
                    <hr />
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ minWidth: 100 }}>
                        Instagram
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {instagramHandles.map((handle, index) => (
                          <Chip
                            key={`ig-${index}`}
                            label={handle.startsWith("@") ? handle : `@${handle}`}
                            className="chip-instagram"
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </>
                );
              })()}
            </CardContent>
          </Card>
          {/* This is where the social media handles card ends. */}
          
        </Grid>
      </Grid>
      <Drawer
        anchor="right"
        open={isArticleDrawerOpen}
        onClose={() => handleDrawerClose("article")}
      >
        <div className="articleSlide">
          <ArticleItem
            article={selectedArticle}
            onArticleUpdated={() => setRefreshKey(k => k + 1)}
          />
        </div>
      </Drawer>
      <Drawer
        anchor="right"
        open={isClientDrawerOpen}
        onClose={() => handleDrawerClose("client")}
      >
        <div className="formSlide">
          <Suspense fallback={<div>Loading...</div>}>
            <EditClient
              open={isClientDrawerOpen}
              clientId={id}
              onClose={() => handleDrawerClose("client")}
              onSaveSuccess={handleEditSaveSuccess}
            />
          </Suspense>
        </div>
      </Drawer>
      <Drawer
        anchor="right"
        open={isReportDrawerOpen}
        onClose={() => handleDrawerClose("report")}
      >
        <div className="formSlide">
          <form onSubmit={handleReportSubmit}>
            <Typography variant="h6" gutterBottom>
              Generate Report
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ButtonGroup fullWidth>
                  <Button
                    variant={
                      selectedReportType === "content"
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() => handleReportTypeChange("content")}
                  >
                    Content
                  </Button>
                  <Button
                    variant={
                      selectedReportType === "slides" ? "contained" : "outlined"
                    }
                    onClick={() => handleReportTypeChange("slides")}
                  >
                    Slides
                  </Button>
                  <Button
                    variant={
                      selectedReportType === "excel" ? "contained" : "outlined"
                    }
                    onClick={() => handleReportTypeChange("excel")}
                  >
                    Excel
                  </Button>
                </ButtonGroup>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Start Date"
                  type="date"
                  name="start_date"
                  value={reportData.start_date}
                  onChange={handleReportFormChange}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  error={reportFormErrors.startDate}
                  helperText={
                    reportFormErrors.startDate && "Start date is required"
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="End Date"
                  type="date"
                  name="end_date"
                  value={reportData.end_date}
                  onChange={handleReportFormChange}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  error={reportFormErrors.endDate}
                  helperText={
                    reportFormErrors.endDate && "End date is required"
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Generating..." : "Generate Report"}
                </Button>
              </Grid>
            </Grid>
          </form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {generatedFilePath && (
                <div style={{ marginTop: 20 }}>
                  <a href={generatedFilePath} download>
                    Download Report
                  </a>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </Drawer>
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteDialogToggle}
        PaperProps={{
          component: "form",
          onSubmit: handleDeleteConfirm,
        }}
      >
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please note that deleting a client is irreversible, and will remove
            all data related to the client. Are you sure you want to
            delete&nbsp;
            <strong>{client ? client.name : "this client"}</strong>?
          </DialogContentText>
          <TextField
            required
            margin="dense"
            id="confirm"
            name="confirm"
            label="Please type DELETE to confirm"
            fullWidth
            variant="standard"
            value={confirmationInput}
            onChange={handleConfirmationInputChange}
          />
        </DialogContent>
        <DialogActions>
          <ButtonGroup variant="outlined">
            <Button onClick={handleDeleteDialogToggle}>Cancel</Button>
            <Button type="submit" color="error">
              Delete
            </Button>
          </ButtonGroup>
        </DialogActions>
      </Dialog>

      <Dialog open={isAddDialogOpen} onClose={handleAddDialogToggle}>
        <DialogTitle>Add Article</DialogTitle>

        <form onSubmit={handleAddSubmit}>
          <DialogContent>
            <p>
              From time to time, some publication's SEO practices may prevent
              their results from being searchable. If you know that a
              publication has covered your client, but you can't find it in the
              search results, you can add it manually.
            </p>
            <TextField
              autoFocus
              margin="dense"
              id="url"
              label="Article URL"
              type="url"
              fullWidth
              variant="standard"
              name="url"
              value={formData.url}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </DialogContent>
          <DialogActions>
            <ButtonGroup variant="outlined">
              <Button onClick={handleAddDialogToggle} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                Submit
              </Button>
            </ButtonGroup>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={isUploadDialogOpen} onClose={handleUploadDialogToggle}>
        <DialogTitle>Upload Articles</DialogTitle>

        <form onSubmit={handleUploadSubmit}>
          <DialogContent>
            <p>
              Upload iSentia report emails as either .eml or .msg files, or
              complete a csv file in the format provided in the below link.
            </p>
            <Button
              variant="outlined"
              component="label"
              disabled={isLoading}
              sx={{ mt: 2 }}
            >
              Select File
              <input
                type="file"
                accept=".eml,.msg,.csv"
                hidden
                name="uploadFile"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, file: e.target.files[0] }))
                }
              />
            </Button>
            <input
              type="hidden"
              name="client_id"
              value={formData.client_id || ""}
            />

            {formData.file && (
              <p style={{ marginTop: "10px", fontStyle: "italic" }}>
                Selected: {formData.file.name}
              </p>
            )}
          </DialogContent>
          <DialogActions>
            <ButtonGroup variant="outlined">
              <Button onClick={handleUploadDialogToggle} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !formData.file || !id}
              >
                Submit
              </Button>
            </ButtonGroup>
          </DialogActions>
        </form>
      </Dialog>
    </Layout>
  );
};

export default ClientItem;
